import * as React from 'react'
import { mutate } from 'swr'
import { useLocale, useTranslation } from 'hooks'
import { useUser } from './useUser'

export const useDeactivations = (params: DeactivateEndpointParams) => {
  const { user } = useUser()
  const { t } = useTranslation()
  const { localiseUrl } = useLocale()

  const [loading, setLoading] = React.useState(false)
  const [error, setError] = React.useState({ message: '', key: '' })

  const performAction = async (
    params: DeactivateEndpointParams,
    method: string,
    endpointSuffix: string
  ) => {
    try {
      setLoading(true)
      setError({ message: '', key: '' })

      const endpoint = `/api/deactivations${endpointSuffix}`

      await fetchAndMutate(endpoint, params, method)
    } catch (e: any) {
      setError({ ...error, message: t('shared.something_wrong') })
    } finally {
      setLoading(false)
    }
  }

  const activate = async () => {
    await performAction(params, 'POST', '')
  }

  const deactivate = async () => {
    await performAction(params, 'PUT', '/turn_off')
  }

  const fetchAndMutate = async (
    endpoint: string,
    params: DeactivateEndpointParams,
    method: string
  ) => {
    const url = localiseUrl(endpoint, { localeAsParam: true })
    const res = await fetch(url, {
      method,
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        resource_id: user.id,
        resource_type: params.resourceType,
        association_type: params.associationType,
        deactivation_reason: params.deactivationReason
      })
    })

    if (!res.ok) {
      const errorRes = await res.json()

      setError({
        message: errorRes.errors[0] || t('shared.something_wrong'),
        key: errorRes.error_keys[0] || ''
      })
    }

    mutate(url)
  }

  return {
    loading,
    error,
    activate,
    deactivate
  }
}

interface DeactivateEndpointParams {
  resourceType: string
  associationType: string
  deactivationReason: string
}
